import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import FileSaver from 'file-saver';
import Color from 'color';
import {
  BlobProvider,
  Page,
  Document,
  View,
  Text,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';

import WeployLogoBlack from 'images/weploy_logo_black.png'; // Only supports jpg or png, no svg :(

import Button from 'WeployDesignLanguage/buttons/Button';
import Snackbar from 'LegacyWeployDesignLanguage/Snackbar';
import dagstorp from 'LegacyWeployDesignLanguage/dagstorp';

import formatCurrency from '../../../utils/Currency';
import introFontWoff2URL from '../../../../../assets/fonts/IntroRegular.ttf';
import introVBoldFontWoff2URL from '../../../../../assets/fonts/IntroBold.ttf';

Font.register({
  family: 'IntroRegular',
  src: introFontWoff2URL,
  fontStyle: 'normal',
  fontWeight: 'normal',
});

Font.register({
  family: 'IntroBold',
  src: introVBoldFontWoff2URL,
  fontStyle: 'normal',
  fontWeight: 'normal',
});

const textPrimaryColor = Color(dagstorp.colors.text.primary).hex();
const textSecondaryColor = '#00B299'; // Color lib can't convert to hex because of alpha channel
const dividerColor = '#d4dbda';

const pxToPt = pixels => (`${Math.round(pixels * 0.75)}pt`);

const commonCellStyles = {
  fontSize: pxToPt(16),
  color: textPrimaryColor,
  fontFamily: 'IntroRegular',
};

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `${pxToPt(1)} solid ${dividerColor}`,
    paddingTop: pxToPt(16),
    paddingBottom: pxToPt(16),
  },
  timesheetEntryCell: {
    ...commonCellStyles,
    flex: 1,
  },
  cell: {
    ...commonCellStyles,
  },
  timesheetBreakPeriods: {
    display: 'flex',
    flexDirection: 'column',
  },
  invoiceRow: {
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `${pxToPt(1)} solid ${dividerColor}`,
    paddingTop: pxToPt(16),
    paddingBottom: pxToPt(16),
  },
  page: {
    padding: pxToPt(24),
  },
  title: {
    fontSize: pxToPt(20),
    color: textPrimaryColor,
    paddingTop: pxToPt(20),
    paddingBottom: pxToPt(20),
    fontFamily: 'IntroBold',
  },
  headerWrapper: {
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: pxToPt(12),
  },
  subtitle1: {
    color: textPrimaryColor,
    fontSize: pxToPt(16),
    marginBottom: pxToPt(16),
    fontFamily: 'IntroBold',
  },
  header: {
    color: textPrimaryColor,
    fontSize: pxToPt(24),
    fontFamily: 'IntroBold',
  },
  approvedByMessage: {
    fontSize: pxToPt(16),
    color: textSecondaryColor,
    marginBottom: pxToPt(16),
    fontFamily: 'IntroBold',
  },
  logo: {
    width: pxToPt(120),
    height: pxToPt(34),
    marginRight: pxToPt(16),
  },
  profile: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingBottom: pxToPt(16),
    paddingTop: pxToPt(32),
  },
  profileText: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    fontFamily: 'IntroRegular',
  },
  h6: {
    fontSize: pxToPt(20),
    color: textPrimaryColor,
    fontFamily: 'IntroBold',
    paddingBottom: pxToPt(16),
  },
  body1: {
    fontSize: pxToPt(16),
    fontFamily: 'IntroRegular',
    color: textPrimaryColor,
    paddingBottom: pxToPt(8),
  },
  timesheetPeriodTable: {
    marginTop: pxToPt(24),
  },
});

const TimesheetEntryHeader = () => (
  <View style={styles.row}>
    <Text style={styles.timesheetEntryCell}>Date</Text>
    <Text style={styles.timesheetEntryCell}>Total (Hrs)</Text>
    <Text style={styles.timesheetEntryCell}>Start/End times</Text>
    <Text style={styles.timesheetEntryCell}>Break</Text>
    <Text style={styles.timesheetEntryCell}>Break Times</Text>
  </View>
);

const TimesheetEntry = ({
  breakPeriods,
  date,
  hours,
  timesheetEntryPeriod,
  totalBreakMinutes,
}) => (
  <View style={styles.row}>
    <Text style={styles.timesheetEntryCell}>{date}</Text>
    <Text style={styles.timesheetEntryCell}>{hours}</Text>
    <Text style={styles.timesheetEntryCell}>{timesheetEntryPeriod}</Text>
    <Text style={styles.timesheetEntryCell}>{totalBreakMinutes}</Text>
    <View style={styles.timesheetBreakPeriods}>
      {breakPeriods.map(breakPeriod => (
        <Text style={styles.timesheetEntryCell} key={breakPeriod}>
          {breakPeriod}
        </Text>
      ))}
    </View>
  </View>
);

TimesheetEntry.propTypes = {
  breakPeriods: PropTypes.array.isRequired,
  date: PropTypes.string.isRequired,
  hours: PropTypes.string.isRequired,
  timesheetEntryPeriod: PropTypes.string.isRequired,
  totalBreakMinutes: PropTypes.string.isRequired,
};

const InvoiceRow = ({ label, amount, currency }) => (
  <View style={styles.invoiceRow}>
    <Text style={styles.cell}>{label}</Text>
    <Text style={styles.cell}>{formatCurrency(amount, currency)}</Text>
  </View>
);

InvoiceRow.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};


const TimesheetSummaryPDF = ({ timesheets }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.headerWrapper}>
        <Text style={styles.header}>Timesheets</Text>
        <Image style={styles.logo} src={WeployLogoBlack} />
      </View>
      <View>
        {
          timesheets.map(({
            id,
            approvedAtDetails,
            baseHourlyRate,
            invoicePeriod,
            currency,
            ordinaryHours,
            ordinaryWages,
            payrollComplianceFee,
            penaltyHours,
            penaltyWages,
            serviceFee,
            submittedAtDetails,
            subtotal,
            subtotalGst,
            timesheetEntries: timesheetEntriesGroupedByPeriod,
            weployeeName,
          }) => (
            <Fragment key={id}>
              <View style={styles.profile}>
                <View style={styles.profileText}>
                  <Text style={styles.h6}>{weployeeName}’s timesheet</Text>
                  <Text style={styles.body1}>{submittedAtDetails}</Text>
                  <Text style={styles.approvedByMessage}>{approvedAtDetails}</Text>
                </View>
              </View>
              <Text style={styles.subtitle1}>Invoice Period: {invoicePeriod}</Text>
              {
                timesheetEntriesGroupedByPeriod && timesheetEntriesGroupedByPeriod.map(({ period, entries }) => (
                  <View key={`${id}-${period}`} style={styles.timesheetPeriodTable}>
                    <Text style={styles.subtitle1}>{period}</Text>
                    <View style={styles.table}>
                      <TimesheetEntryHeader />
                      {
                        entries.map(entry => (
                          <TimesheetEntry key={entry.id} {...entry} />
                        ))
                      }
                    </View>
                  </View>
                ))
              }

              <Text style={styles.title}>Invoice</Text>
              <View style={styles.table}>
                <InvoiceRow
                  label={`Ordinary Hours x ${ordinaryHours} @ ${formatCurrency(baseHourlyRate, currency)}ph`}
                  amount={ordinaryWages}
                  currency={currency}
                />
                <InvoiceRow
                  label={`Penalty hours x ${penaltyHours}`}
                  amount={penaltyWages}
                  currency={currency}
                />
                <InvoiceRow
                  label="Payroll compliance fee (Super, Payroll Tax, and Work Cover)"
                  amount={payrollComplianceFee}
                  currency={currency}
                />
                <InvoiceRow
                  label="Service Fee"
                  amount={serviceFee}
                  currency={currency}
                />
                <InvoiceRow
                  label="GST"
                  amount={subtotalGst}
                  currency={currency}
                />
                <InvoiceRow
                  label="Total"
                  amount={subtotal + subtotalGst}
                  currency={currency}
                />
              </View>
            </Fragment>
          ))
        }
      </View>
    </Page>
  </Document>
);

TimesheetSummaryPDF.propTypes = {
  timesheets: PropTypes.array.isRequired,
};

const TimesheetSummaryDownload = ({
  filePrefix,
  isEnabled,
  onExportClick,
  timesheets,
}) => {
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [shouldRenderPDFBlob, setShouldRenderPDFBlob] = useState(false);

  const handleSaveFile = (filename, pdfBlobData) => {
    // The loadingModalVisible state change to false will trigger this
    // to execute again before the modal gets a chance to close
    if (snackbarVisible) {
      const blob = new Blob([pdfBlobData], { type: 'text/plain;charset=utf-8' });
      FileSaver.saveAs(blob, filename);

      onExportClick();
      setSnackbarVisible(false);
      setShouldRenderPDFBlob(false);
    }
  };

  return (
    <>
      <Button
        appearance={Button.TEXT}
        onClick={() => setSnackbarVisible(true)}
        disabled={!isEnabled || snackbarVisible}
      >
        Export
      </Button>
      <Snackbar
        visible={snackbarVisible}
        message="Generating PDF..."
        onEntered={() => setShouldRenderPDFBlob(true)}
      />
      { shouldRenderPDFBlob && (
        <BlobProvider document={<TimesheetSummaryPDF timesheets={timesheets} />}>
          {({ blob, loading }) => {
            if (!loading && blob) {
              handleSaveFile(`${filePrefix}-${moment().format('YYYY-MM-dd-HH:mm:ss')}.pdf`, blob);
            }

            return null;
          }}
        </BlobProvider>
      )}
    </>
  );
};

TimesheetSummaryDownload.propTypes = {
  isEnabled: PropTypes.bool,
  filePrefix: PropTypes.string.isRequired,
  onExportClick: PropTypes.func.isRequired,
  timesheets: PropTypes.array.isRequired,
};

TimesheetSummaryDownload.defaultProps = {
  isEnabled: PropTypes.bool,
};

export default TimesheetSummaryDownload;
